<script setup>
import { reactive, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { BubbleMenu } from '@tiptap/vue-3/menus'
import { inGridSkippingTable } from './extensions/tableGrid.js'
import { elementAnchor, menuOptions } from './floatingMenu.js'

// RED-317 PR 3b — the table tools, floating at the active table's top-right
// corner (the RED-268 TableMenu pattern: anchored to the table, not the
// caret, an own bubble-menu plugin key). Rows, columns, merge/split, the
// repeated header row (w:tblHeader) and cell fill (contract §4). A Word table
// whose rows skip grid columns keeps its rows and columns (tableGrid.js):
// those buttons are disabled and a lock says where to change them.
const props = defineProps({
  editor: { type: Object, required: true },
  // The element the canvas scrolls in (an Element or window); read when the menu mounts.
  scrollTarget: { type: Object, default: null },
})

const { t } = useI18n()

/** The cell fills offered: none, the malls' header orange, greys and pale tints. */
const FILLS = ['auto', 'FFC000', 'F2F2F2', 'D9D9D9', 'BFBFBF', 'DEEAF6', 'E2EFD9', 'FBE4D5', 'FFF2CC']

const can = reactive({
  addRow: false,
  deleteRow: false,
  addColumn: false,
  deleteColumn: false,
  merge: false,
  split: false,
  headerRow: false,
  locked: false,
})

const fillPopover = useTemplateRef('fillPopover')

const shouldShow = ({ editor: instance, state }) => {
  const show = instance.isEditable && instance.isActive('table')
  if (show) {
    can.addRow = instance.can().addRowAfter()
    can.deleteRow = instance.can().deleteRow()
    can.addColumn = instance.can().addColumnAfter()
    can.deleteColumn = instance.can().deleteColumn()
    can.merge = instance.can().mergeCells()
    can.split = instance.can().splitCell()
    can.headerRow = instance.can().toggleHeaderRow()
    can.locked = inGridSkippingTable(state)
  }
  return show
}

// Anchor: the table's DOM node, resolved from the selection.
const tableVirtualElement = () => {
  const { state, view } = props.editor
  const { $head } = state.selection
  for (let depth = $head.depth; depth > 0; depth--) {
    if ($head.node(depth).type.spec.tableRole === 'table') {
      const dom = view.nodeDOM($head.before(depth))
      if (dom instanceof HTMLElement) {
        return elementAnchor(dom)
      }
    }
  }
  return undefined
}

const run = (command) => props.editor.chain().focus()[command]().run()

const setFill = (fill) => {
  props.editor.chain().focus().setCellAttribute('fill', fill).run()
  fillPopover.value?.hide()
}

const buttonClass =
  'flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-35'
</script>

<template>
  <BubbleMenu
    :editor="editor"
    plugin-key="studioTableMenu"
    :should-show="shouldShow"
    :get-referenced-virtual-element="tableVirtualElement"
    :update-delay="100"
    :options="menuOptions(scrollTarget)"
  >
    <div class="flex items-center gap-0.5 rounded-full border border-gray-200 bg-white px-1.5 py-1 shadow-lg" role="toolbar" :aria-label="t('messages.editor.tableMenu')" data-testid="studio-table-menu">
      <span v-if="can.locked" v-tooltip.top="t('messages.editor.tableLocked')" class="flex h-7 w-7 items-center justify-center text-gray-500" tabindex="0" :aria-label="t('messages.editor.tableLocked')" data-testid="studio-table-locked">
        <span class="pi pi-lock text-xs" />
      </span>
      <button v-tooltip.top="t('messages.editor.tableRowAdd')" type="button" :class="buttonClass" :disabled="!can.addRow" :aria-label="t('messages.editor.tableRowAdd')" data-testid="studio-table-row-add" @click="run('addRowAfter')">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
          <rect x="2" y="2.5" width="12" height="4.5" rx="1" />
          <path d="M8 9.5v4.5M5.75 11.75h4.5" />
        </svg>
      </button>
      <button v-tooltip.top="t('messages.editor.tableRowRemove')" type="button" :class="buttonClass" :disabled="!can.deleteRow" :aria-label="t('messages.editor.tableRowRemove')" data-testid="studio-table-row-remove" @click="run('deleteRow')">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
          <rect x="2" y="2.5" width="12" height="4.5" rx="1" />
          <path d="M5.75 11.75h4.5" />
        </svg>
      </button>

      <span class="mx-0.5 h-3.5 w-px bg-gray-200" aria-hidden="true" />

      <button v-tooltip.top="t('messages.editor.tableColAdd')" type="button" :class="buttonClass" :disabled="!can.addColumn" :aria-label="t('messages.editor.tableColAdd')" data-testid="studio-table-col-add" @click="run('addColumnAfter')">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
          <rect x="2.5" y="2" width="4.5" height="12" rx="1" />
          <path d="M9.5 8H14M11.75 5.75v4.5" />
        </svg>
      </button>
      <button v-tooltip.top="t('messages.editor.tableColRemove')" type="button" :class="buttonClass" :disabled="!can.deleteColumn" :aria-label="t('messages.editor.tableColRemove')" data-testid="studio-table-col-remove" @click="run('deleteColumn')">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
          <rect x="2.5" y="2" width="4.5" height="12" rx="1" />
          <path d="M9.5 8H14" />
        </svg>
      </button>

      <span class="mx-0.5 h-3.5 w-px bg-gray-200" aria-hidden="true" />

      <button v-tooltip.top="t('messages.editor.tableMerge')" type="button" :class="buttonClass" :disabled="!can.merge" :aria-label="t('messages.editor.tableMerge')" data-testid="studio-table-merge" @click="run('mergeCells')">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
          <rect x="1.5" y="3" width="13" height="10" rx="1" />
          <path d="M5 8h6M9.5 6.5 11 8l-1.5 1.5M6.5 6.5 5 8l1.5 1.5" />
        </svg>
      </button>
      <button v-tooltip.top="t('messages.editor.tableSplit')" type="button" :class="buttonClass" :disabled="!can.split" :aria-label="t('messages.editor.tableSplit')" data-testid="studio-table-split" @click="run('splitCell')">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
          <rect x="1.5" y="3" width="13" height="10" rx="1" />
          <path d="M8 3v10" />
        </svg>
      </button>
      <button v-tooltip.top="t('messages.editor.tableHeaderRow')" type="button" :class="buttonClass" :disabled="!can.headerRow" :aria-label="t('messages.editor.tableHeaderRow')" data-testid="studio-table-header-row" @click="run('toggleHeaderRow')">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
          <rect x="1.5" y="2.5" width="13" height="11" rx="1" />
          <path d="M1.5 6.5h13" />
          <path d="M3.5 4.5h9" stroke-width="2.2" />
        </svg>
      </button>
      <button v-tooltip.top="t('messages.editor.cellFill')" type="button" :class="buttonClass" :aria-label="t('messages.editor.cellFill')" aria-haspopup="true" data-testid="studio-table-fill" @click="fillPopover.toggle($event)">
        <span class="pi pi-palette text-xs" />
      </button>
      <Popover ref="fillPopover" data-testid="studio-table-fill-popover">
        <div class="flex items-center gap-1.5">
          <button
            v-for="fill in FILLS"
            :key="fill"
            v-tooltip.bottom="fill === 'auto' ? t('messages.editor.fillNone') : `#${fill}`"
            type="button"
            class="flex h-6 w-6 cursor-pointer items-center justify-center rounded-sm border border-gray-300"
            :style="{ backgroundColor: fill === 'auto' ? '#ffffff' : `#${fill}` }"
            :aria-label="fill === 'auto' ? t('messages.editor.fillNone') : `#${fill}`"
            :data-testid="`studio-fill-${fill}`"
            @click="setFill(fill)"
          >
            <span v-if="fill === 'auto'" class="pi pi-ban text-[0.6rem] text-gray-500" />
          </button>
        </div>
      </Popover>

      <span class="mx-0.5 h-3.5 w-px bg-gray-200" aria-hidden="true" />

      <button v-tooltip.top="t('messages.editor.tableDelete')" type="button" :class="buttonClass" :aria-label="t('messages.editor.tableDelete')" data-testid="studio-table-delete" @click="run('deleteTable')">
        <span class="pi pi-trash text-xs" />
      </button>
    </div>
  </BubbleMenu>
</template>
